/*!
 * tone-autostop.js — stop a tone tool that has been left running.
 *
 * WHY THIS EXISTS
 * ---------------
 * These tools emit a continuous tone for as long as the tab is open, and the
 * telemetry says people leave them open. Measured over 30 days of
 * `tool_abandon` (time on page, which bounds time playing):
 *
 *     /dog-whistle          24 sessions   avg 228 min   max  70 h
 *     /tone-generator       71 sessions   avg 106 min   max  70 h
 *     /rodent-repellent     87 sessions   avg  54 min   max  18 h
 *     /dog-repellent        57 sessions   avg  52 min   max  28 h
 *
 * And what they emit is not music. Measured against real music normalised to
 * full scale (tests/scratch/worst-case-bench.mjs, 2026-08-22), as coil
 * dissipation per minute — with the voice coil's rising impedance accounted
 * for, so the ultrasonic tools are not overcharged:
 *
 *     /dog-whistle at its 100 Hz slider floor, full volume   22.9x music
 *     /tone-generator at full volume                         19.2x
 *     /dog-repellent at its DEFAULT volume of 100            14.8x
 *     /rodent-repellent full volume                          13.8x
 *     /cockroach-repellent full volume                        9.8x
 *
 * Nobody means to run a test tone for seventy hours. This does not change what
 * a tool emits while it runs — it bounds the case where the person walked away.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * Silence the tool quietly. A tool that stops on its own without saying so is
 * indistinguishable from a broken one, which is a complaint this codebase has
 * already collected (/speaker-cleaner #1457). onFire() must leave the tool in
 * a visibly stopped state, and a toast says why.
 *
 * USAGE
 *     var auto = ToneAutostop.create({
 *         minutes: 60,
 *         onFire: stopEverything,      // put the tool in its stopped state
 *         notice: 'Остановлено автоматически…'
 *     });
 *     auto.arm();      // playback started
 *     auto.disarm();   // the user stopped it
 *     auto.remainingMs();
 *
 * Classic script (ES5, no imports) — safe for @light() + the ES2019 guard.
 */
(function (root) {
    'use strict';

    function create(opts) {
        var minutes = (opts && opts.minutes) || 60;
        var onFire = (opts && opts.onFire) || function () {};
        var notice = (opts && opts.notice) || '';
        var slug = (opts && opts.slug) || '';
        var timer = null, firesAt = 0;

        function disarm() {
            if (timer) { clearTimeout(timer); timer = null; }
            firesAt = 0;
        }

        function arm() {
            disarm();
            firesAt = Date.now() + minutes * 60000;
            timer = setTimeout(function () {
                timer = null; firesAt = 0;
                try { onFire(); } catch (e) {}
                // Say it out loud. A silent stop reads as a broken tool.
                try { if (notice && root.__toast) root.__toast(notice, 'info'); } catch (e) {}
                try {
                    if (root.__aeFeature) root.__aeFeature('tone_autostop', { slug: slug, minutes: minutes });
                } catch (e) {}
            }, minutes * 60000);
        }

        function remainingMs() {
            return firesAt ? Math.max(0, firesAt - Date.now()) : 0;
        }

        return { arm: arm, disarm: disarm, remainingMs: remainingMs, minutes: minutes };
    }

    root.ToneAutostop = { create: create };
})(typeof window !== 'undefined' ? window : this);
